/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { Grid, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PopupTopBar from './PopupTopBar';
import Steps from '../../common/Steps';
import LeaderboardInfo from './LeaderboardInfo';
import AddDealerships from './AddDealerships';
import RoleLevelConfig from './RoleLevelConfig';
import { setLeaderboardId, showProgress } from '../../../redux/actions/DealershipActions';
import Service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { ErrorToast, notifySuccess } from '../../utils/Utils';
import {
  convertToFormData,
  formatDate,
  scoreIsDisplayed,
  scoreParamEnabled,
  scoreParamLogic,
  transformData,
  typeLabel,
  updateArrayState,
  validateStepOne,
} from '../utils';
import useFetchDepartments from '../hooks/useFetchDepartments';
import PopupFooter from './PopupFooter';
import BottomSheetContainer from './BottomSheetContainer';
import useFetchList from '../hooks/useFetchList';

const createCustomStyles = (height) => ({
  content: {
    height: `${height}px`,
    width: '1070px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0px',
  },
});
const useStyles = makeStyles(() => ({
  popupMainContainer: {
    background: 'rgba(242, 244, 247, 1)',
    width: '100%',
  },
  popupStepsContainer: {
    position: 'sticky',
    top: '85px',
    zIndex: 10,
    padding: '20px',
    background: 'rgba(255, 255, 255, 1)',
    width: '100%',
  },
}));

const LeaderboardAddNewPopup = ({ close, leaderboardId = null }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { list, details, getDetails, getList, getOverview } = useFetchList();
  const { departmentData } = useFetchDepartments();
  const overview = useSelector((state) => state?.getLeaderboard.leaderboardOverview);
  const startDate = formatDate(new Date());
  const endDate = null;

  const [data, setData] = useState({
    logo: null,
    visibility: [],
    enabled: 1,
    isDefault: false,
    category: 'DEFAULT',
    scoreBreakupParameters: [],
    departmentRoleIds: [],
    dealerships: [],
    startDate,
    endDate,
    timezone: 'America/Denver',
  });

  const [step, setStep] = useState(1);
  const [filters, setFilters] = useState({});
  const [filterObjectInitial, setFilterObjectInitial] = useState({});
  const [logoFilename, setLogoFilename] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [department, setDepartment] = useState([]);
  const modalRef = useRef(null);

  useEffect(() => {
    if (departmentData) setDepartment(departmentData);
  }, [departmentData]);

  const getFilters = useCallback(async () => {
    dispatch(showProgress(true));
    try {
      const res = await Service.get(apiEndpoints.DEALER_FILTER);
      const { filterData, filterObject } = transformData(res);
      setFilters(filterData || {});
      setFilterObjectInitial(filterObject || {});
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  }, [dispatch]);

  useEffect(() => {
    if (leaderboardId) {
      getDetails(leaderboardId);
    }
    getFilters();
  }, [getFilters, getDetails, leaderboardId]);

  useEffect(() => {
    if (leaderboardId) {
      const newData = {
        ...details,
        isDefault: details?.isDefault === 1,
        dealerships: overview?.dealerships?.flatMap((item) => item.id) || [],
        category: 'DEFAULT',
        highlightTopScoreLogic: details?.highlightTopScoreLogic ? JSON.stringify(details?.highlightTopScoreLogic) : '',
      };
      setData(newData);
    }
  }, [details, leaderboardId, overview?.dealerships]);

  useEffect(() => {
    if (leaderboardId === null) {
      const roleIds = department?.flatMap((dept) => dept?.roles?.map((role) => role.id));
      setData((prevData) => ({ ...prevData, departmentRoleIds: roleIds }));
    }
  }, [department, leaderboardId]);

  const toggleSheet = () => setSheetOpen((prev) => !prev);

  const handleFileChange = (name, files, setFilename) => {
    if (files.length > 0) {
      const fileData = files[0];
      setFilename(fileData?.name);
      setData((prevData) => ({
        ...prevData,
        [name]: fileData,
      }));
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (name === 'logo') {
          setThumbnail(ev.target.result);
        }
      };
      reader.readAsDataURL(fileData);
    }
  };

  const handleDataChange = useCallback(
    ({ target }) => {
      const { name, value, checked, files } = target;
      if (name === 'logo') {
        handleFileChange(name, files, setLogoFilename);
      } else if (name === 'scoreBreakupParameters' || name?.endsWith('Displayed')) {
        const type = name?.endsWith('Displayed') ? `${name.replace('Displayed', '')}` : value;
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreIsDisplayed(data.scoreBreakupParameters, type, checked),
        }));
      } else if (name?.endsWith('Stars')) {
        const type = name.replace('Stars', '');
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreParamLogic(data.scoreBreakupParameters, type, value),
        }));
      } else if (name?.endsWith('Enabled')) {
        const type = name.replace('Enabled', '');
        setData((prevData) => ({
          ...prevData,
          scoreBreakupParameters: scoreParamEnabled(data?.scoreBreakupParameters, type, checked),
        }));
      } else if (name === 'departmentRoleIds' || name === 'dealerships') {
        setData((prevData) => updateArrayState(prevData, name, value, checked));
      } else if (name === 'visibility') {
        const updatedVisibility = checked
          ? [...(data.visibility || []), value]
          : (data.visibility || []).filter((item) => item !== value);
        setData((prevData) => ({ ...prevData, visibility: updatedVisibility }));
      } else if (name === 'type') {
        setData((prevData) => ({
          ...prevData,
          [name]: typeLabel(value),
        }));
      } else if (name === 'isDefault') {
        setData((prevData) => ({
          ...prevData,
          isDefault: checked,
        }));
      } else {
        setData((prevData) => ({ ...prevData, [name]: value }));
      }
    },
    [data],
  );

  const handleDateChange = (label, date) => setData({ ...data, [label]: date });
  const handleDeleteLogo = () => {
    setData({
      ...data,
      logo: null,
    });
    setLogoFilename('');
  };

  const saveLB = async () => {
    if (step === 1 && !validateStepOne(data, leaderboardId === null, list)) return;
    setStep((pre) => (pre + 1 !== 4 ? pre + 1 : pre));
    if (step === 3) {
      const formData = convertToFormData(data, leaderboardId !== null);
      const endPoint =
        leaderboardId != null ? apiEndpoints.LB_UPDATE.replace('ID', leaderboardId) : apiEndpoints.LB_CREATE;
      const msg = leaderboardId === null ? 'Leaderboard Added Successfully' : 'Leaderboard Updated Successfully';
      dispatch(showProgress(false));
      try {
        dispatch(showProgress(true));
        const res = await Service.post(endPoint, formData);
        notifySuccess(msg);
        getList(false);
        if (leaderboardId !== null) {
          getOverview(leaderboardId);
        } else {
          getOverview(res?.id);
          dispatch(setLeaderboardId(res?.id));
        }
        close(true);
        dispatch(showProgress(false));
      } catch (error) {
        ErrorToast(error);
        dispatch(showProgress(false));
      }
    }
  };
  const backBtn = async () => setStep((pre) => (pre - 1 !== 0 ? pre - 1 : pre));
  const handleDeptChange = (deptId, selected, departments) => {
    setData((prevData) => {
      const selectedDept = departments.find((dept) => dept.id === deptId);
      const roleIds = selectedDept?.roles.map((role) => role.id) || [];
      return {
        ...prevData,
        departmentRoleIds: selected
          ? [...new Set([...prevData.departmentRoleIds, ...roleIds])]
          : prevData.departmentRoleIds.filter((id) => !roleIds.includes(id)),
      };
    });
  };
  const handleSelectAll = (updatedIds, checked) => {
    setData((prevData) => ({
      ...prevData,
      dealerships: checked
        ? [...new Set([...prevData.dealerships, ...updatedIds])]
        : prevData.dealerships.filter((id) => !updatedIds.includes(id)),
    }));
  };
  const handleRemoveDealership = ({ target: { id } }, name) => {
    if (name === 'Remove') {
      setData((prevData) => {
        const updatedIds = prevData.dealerships?.filter((item) => item !== parseInt(id, 10));
        return { ...prevData, dealerships: updatedIds };
      });
    }
  };
  return (
    <Modal
      isOpen
      onRequestClose={() => false}
      shouldCloseOnEsc={false}
      style={step === 3 ? createCustomStyles(825) : createCustomStyles(723)}
    >
      <div ref={modalRef}>
        <PopupTopBar add={leaderboardId === null} title="Leaderboard" close={close} />

        <Grid container className={classes.popupMainContainer}>
          <Grid item className={classes.popupStepsContainer}>
            <Steps step={step} stepInfo={['Leaderboard Info', 'Role Configuration', 'Dealerships']} />
          </Grid>

          {step === 1 && (
            <Grid item style={{ minHeight: '628px' }}>
              <LeaderboardInfo
                data={data}
                handleDataChange={handleDataChange}
                handleDateChange={handleDateChange}
                handleDeleteLogo={handleDeleteLogo}
                logoFilename={logoFilename}
                thumbnail={thumbnail}
              />
            </Grid>
          )}
          {step === 2 && (
            <Grid item style={{ minHeight: '471px' }}>
              <RoleLevelConfig
                data={data}
                handleChange={handleDataChange}
                departmentRoleIds={details.departmentRoleIds}
                handleDeptChange={handleDeptChange}
                departmentData={department}
              />
            </Grid>
          )}
          {step === 3 && (
            <Grid item style={{ width: '100%' }}>
              <AddDealerships
                data={data}
                handleDataChange={handleDataChange}
                handleSelectAll={handleSelectAll}
                filters={filters}
                filterObjectInitial={filterObjectInitial}
              />
            </Grid>
          )}
        </Grid>
        {step === 3 && (
          <BottomSheetContainer
            data={data}
            isSheetOpen={isSheetOpen}
            toggleSheet={toggleSheet}
            handleRemove={handleRemoveDealership}
          />
        )}
        <PopupFooter backBtn={backBtn} saveLB={saveLB} step={step} />
      </div>
    </Modal>
  );
};

export default LeaderboardAddNewPopup;
